import { takeEvery, put } from 'redux-saga/effects';
import openSocket from 'socket.io-client';
import { fetchPlayerNotifications, addNotificationToStore } from '../../../../../store/slices/playerNotifications';
import { addNotificationToQueue, pauseInboundNotifications, resumeInboundNotifications } from '../../../../../store/slices/notifications';
// NotificationManager will handle opening modals and sequencing. No popup control here.
import * as actionTypes from '../../../../../modules/casino/store/actions/actionTypes';
import { store } from '../../../../../store';

const DEBUG = true;
let socket = null;
let isConnecting = false;

const isPathPausedByConfig = (pathname) => {
  try {
    const nc = window.config && window.config.notificationCenter;
    const nested = nc && Array.isArray(nc.notificationsPausedOnRoutes) && nc.notificationsPausedOnRoutes.length
      ? nc.notificationsPausedOnRoutes
      : null;
    const root = window.config && Array.isArray(window.config.notificationsPausedOnRoutes) && window.config.notificationsPausedOnRoutes.length
      ? window.config.notificationsPausedOnRoutes
      : null;
    const patterns = nested || root || [];
    const p = (pathname || '').toLowerCase();
    return patterns.some((pat) => {
      if (!pat) return false;
      const s = String(pat).toLowerCase();
      if (s.endsWith('*')) return p.startsWith(s.slice(0, -1));
      return p.includes(s);
    });
  } catch (e) {
    return false;
  }
};

// Shared handler for inbound notification-like events (toast or modal)
const handleInboundNotification = (eventType, data) => {
  const title = data?.content?.title || 'New Notification';
  const body = data?.content?.body || 'You have a new notification';

  const notification = {
    id: data.id,
    type: data.type || eventType || 'toast',
    vertical: data.vertical || 'CASINO',
    category: data.category || 'Other',
    subcategory: data.subcategory || null,
    data,
    read: false,
    source: 'websocket',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    user_id: null,
    reference_id: null,
  };

  // Decide modal strictly by declared type or event channel
  const isModal = (data.type === 'modal') || (eventType === 'modal');

  // Derive modal popup id (if any). Used only to target which modal to open.
  const displayModalAction = data.actions && data.actions.find((a) => a.handler?.method === 'displayModal');
  const modalActionParam = displayModalAction?.handler?.params?.[0];
  const derivedPopupId = modalActionParam || data.identifier || data.modalPopupId;

  // Add to notifications list (for the notifications page)
  try {
    store.dispatch(addNotificationToStore({ notification }));
  } catch (e) {
    // ignore
  }

  // Build a queue item. For modal, mark as persistent and include popup id.
  const notificationData = {
    id: data.id,
    title,
    message: body,
    type: isModal ? 'modal' : (data.type || 'info'),
    duration: isModal ? undefined : (data.duration || 5000),
    priority: data.priority || 'normal',
    persistent: isModal ? true : (data.persistent || false),
    actions: data.actions || [],
    identifier: data.identifier || undefined,
    modalPopupId: isModal ? (derivedPopupId || undefined) : undefined,
    icon: data.content?.icon || undefined,
    image: data.content?.image || undefined,
    suppressToast: isModal ? true : false,
  };

  // Enqueue; allow immediate display when idle (manager handles sequencing for follow-ups)
  const st = store.getState();
  const storePaused = !!st.notifications?.isInboundPaused;
  const path = (window.location.pathname || '').toLowerCase();
  const routePaused = isPathPausedByConfig(path);
  const paused = storePaused || routePaused;

  if (routePaused && !storePaused) {
    try { store.dispatch(pauseInboundNotifications()); } catch { }
  } else if (!routePaused && storePaused) {
    try { store.dispatch(resumeInboundNotifications()); } catch { }
  }

  store.dispatch(addNotificationToQueue({ notification: notificationData, suppressImmediateDisplay: paused }));
};

export const connect = () => {
  if (socket !== null || isConnecting) {
    DEBUG && console.log('Socket already exists or is connecting, skipping connection');
    return;
  }

  isConnecting = true;
  DEBUG && console.log('Starting notifications WebSocket connection...');

  const query = {
    protocol: 'sio1',
  };

  if (window.config && window.config.clientId) {
    query['tenantId'] = window.config.clientId;
  }

  socket = openSocket(window.config.playerWsUrl, {
    path: window.config.playerWsPath,
    forceNew: true,
    transports: ['websocket'],
    allowEIO3: true,
    query,
  });

  DEBUG && console.log('Notifications socket created', socket);

  // Try different join formats
  socket.on('connect', () => {
    console.log('🔌 Notifications WebSocket connected!');
    DEBUG && console.log('Notifications WebSocket connected');
    isConnecting = false;

    const state = store.getState();
    const { authentication } = state;

    if (authentication && ['user', 'token'].indexOf(authentication.auth_type) > -1) {
      socket.emit('login', {
        token: 'Bearer ' + authentication.access_token,
      });
      console.log('🔑 Login emitted for notifications WebSocket');
      DEBUG && console.log('Emitted login for notifications WebSocket');
      DEBUG && console.log('Current user ID:', authentication.user_id || 'unknown');
      DEBUG && console.log('Current tenant ID:', window.config?.clientId || 'unknown');
    } else {
      console.log('❌ No authentication found, skipping login');
      DEBUG && console.log('No authentication found, skipping login');
    }
  });

  // Listen for login response
  socket.on('login_success', (data) => {
    DEBUG && console.log('Login successful:', data);
  });

  socket.on('login_error', (error) => {
    console.error('Login failed:', error);
  });

  // Also try to join immediately (in case login_success event doesn't exist)
  socket.on('authenticated', (data) => {
    DEBUG && console.log('Authenticated:', data);
  });

  socket.on('connect_error', (error) => {
    console.error('Notifications WebSocket connect error', error);
  });

  socket.on('error', (error) => {
    console.error('Notifications WebSocket error', error);
  });

  socket.on('disconnect', () => {
    DEBUG && console.log('Notifications WebSocket disconnected');
  });

  // Listen for toast notifications (the actual notification events)
  socket.on('toast', (data) => {
    DEBUG && console.log('[WS] toast event received', data);
    handleInboundNotification('toast', data);
  });

  // Also listen for modal notifications explicitly
  socket.on('modal', (data) => {
    DEBUG && console.log('[WS] modal event received', data);
    handleInboundNotification('modal', data);
  });
};

export const disconnect = () => {
  if (socket !== null) {
    socket.disconnect();
    socket = null;
  }
  isConnecting = false;
};

function* notificationsInitializeSaga() {
  DEBUG && console.log('Initializing notifications WebSocket - saga called');
  try {
    yield connect();
    DEBUG && console.log('Notifications WebSocket connect() called successfully');
  } catch (error) {
    console.error('Error initializing notifications WebSocket:', error);
  }
}

function* notificationsPlayerLoginSaga(action) {
  DEBUG && console.log('Notifications WS login', action, socket);

  if (socket === null) {
    connect();
    return;
  }

  if (['user', 'token'].indexOf(action.authType) > -1) {
    yield socket.emit('login', {
      token: 'Bearer ' + action.accessToken,
    });
  }
}

function* notificationsPlayerLogoutSaga() {
  DEBUG && console.log('Notifications WS logout');

  if (socket === null) {
    return;
  }

  socket.emit('logout');
}

export default function* watchNotificationsWebSocket() {
  DEBUG && console.log('[saga] watching notifications ws');
  yield takeEvery(actionTypes.application.INITIALIZE, notificationsInitializeSaga);
  yield takeEvery(actionTypes.authentication.AUTHENTICATE, notificationsPlayerLoginSaga);
  yield takeEvery(actionTypes.authentication.CLEAR, notificationsPlayerLogoutSaga);
}
